import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import ChatThread from "@/components/messaging/ChatThread";
import { MessageSquare } from "lucide-react";

// Per-project team chat: a header button that slides in the whole-team channel bound to this bid.
export default function ProjectChatButton({ bidId }) {
  const [open, setOpen] = useState(false);
  const [channelId, setChannelId] = useState(null);
  const [unread, setUnread] = useState(0);
  const [err, setErr] = useState(false);

  const ensureChannel = useCallback(async () => {
    if (channelId) return channelId;
    try { const { data } = await api.get(`/messaging/project/${bidId}`); setChannelId(data.channel_id); return data.channel_id; }
    catch { setErr(true); return null; }
  }, [bidId, channelId]);

  // Poll this project channel's unread count for the button badge (lightweight, ~20s).
  const refreshUnread = useCallback(async () => {
    const cid = await ensureChannel();
    if (!cid) return;
    try { const { data } = await api.get("/messaging/unread"); setUnread((data.by_channel || {})[cid] || 0); }
    catch { /* ignore */ }
  }, [ensureChannel]);

  useEffect(() => { refreshUnread(); const t = setInterval(refreshUnread, 20000); return () => clearInterval(t); }, [refreshUnread]);
  useEffect(() => { if (open) { ensureChannel(); setUnread(0); } }, [open, ensureChannel]);

  if (err) return null;

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="project-chat-button" className="relative">
        <MessageSquare className="w-4 h-4 mr-1.5" /> Team chat
        {unread > 0 && (
          <span data-testid="project-chat-badge" className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F4C400] text-black text-[10px] font-bold grid place-items-center">{unread > 9 ? "9+" : unread}</span>
        )}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="project-chat-panel">
          <SheetHeader className="px-4 py-3 border-b border-slate-200 space-y-0">
            <SheetTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#003E87]" /> Team chat</SheetTitle>
            <SheetDescription className="text-xs">The whole team can talk here about this project.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 min-h-0">
            {channelId && <ChatThread key={channelId} channelId={channelId} onActivity={() => setUnread(0)} />}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
